import { useState } from "react";
import {
  resetDeviceConfig,
  updateDeviceConfig,
  updateDevicePlayerPref,
} from "@utils/api-communication/RestApiCli";
import { ManagementConfig } from "types/productManagementTypes";
import { isDeviceOfflineError, playerPrefDelivery } from "@utils/deviceConfigKeys";

export type ActionNotification = { type: "success" | "error"; msg: string };

const offlineMessage = (key: string) =>
  `Device is offline — ${key} was not delivered and nothing was saved. Send it again once the headset is connected.`;

export const playerPrefOutcome = (
  key: string,
  value: string,
  response: unknown,
): ActionNotification => {
  switch (playerPrefDelivery(response as { data?: unknown })) {
    case "delivered":
      return { type: "success", msg: `Sent ${key}=${value} to the device` };
    case "queued":
      return {
        type: "success",
        msg: `Device is offline — ${key}=${value} saved and will be applied when it reconnects`,
      };
    default:
      return { type: "error", msg: offlineMessage(key) };
  }
};

// Backends before SD-1782 rejected with 409 instead of saving, so the offline
// case has to be recognised on the error path too.
export const playerPrefError = (key: string, error: unknown): ActionNotification => ({
  type: "error",
  msg: isDeviceOfflineError(error) ? offlineMessage(key) : `Failed to send ${key} to the device`,
});

export const useProductConfigActions = () => {
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState<ActionNotification | null>(null);

  const pushConfig = (pid: string | undefined, cfg: ManagementConfig, onSuccess?: () => void) => {
    if (!pid) {
      setNotification({ type: "error", msg: "This entry has no PID; cannot push to a device" });
      return;
    }
    setSubmitting(true);
    updateDeviceConfig(
      { pid, type: "apply", config: cfg as unknown as Record<string, unknown> },
      (response) => {
        setSubmitting(false);
        const sentToDevice = response?.data === true;
        setNotification({
          type: "success",
          msg: sentToDevice
            ? "Config sent to the device — it will show as Applied once the device confirms"
            : "Device is offline — config saved and will be applied when it reconnects",
        });
        onSuccess?.();
      },
      () => {
        setSubmitting(false);
        setNotification({ type: "error", msg: "Failed to update device config" });
      },
    );
  };

  const sendPlayerPref = (
    pid: string | undefined,
    key: string,
    value: string,
    onSent?: () => void,
  ) => {
    if (!pid) {
      setNotification({ type: "error", msg: "This entry has no PID; cannot push to a device" });
      return;
    }
    setSubmitting(true);
    updateDevicePlayerPref(
      { pid, key, value },
      (response) => {
        setSubmitting(false);
        const outcome = playerPrefOutcome(key, value, response);
        setNotification(outcome);
        if (outcome.type === "success") onSent?.();
      },
      (error) => {
        setSubmitting(false);
        setNotification(playerPrefError(key, error));
      },
    );
  };

  const resetConfig = (pid: string, onSuccess?: () => void) => {
    setSubmitting(true);
    resetDeviceConfig(
      { pid },
      () => {
        setSubmitting(false);
        setNotification({ type: "success", msg: "Device config reset" });
        onSuccess?.();
      },
      () => {
        setSubmitting(false);
        setNotification({ type: "error", msg: "Failed to reset device config" });
      },
    );
  };

  return { submitting, notification, setNotification, pushConfig, sendPlayerPref, resetConfig };
};

export default useProductConfigActions;
